import { Fragment, useMemo, useState } from 'react';
import type { ApiClient } from '../../api';
import type { MsrRaumZeile } from './typen';
import {
  DataTable, EmptyState, FilterBar, StatusBadge,
  type FilterDefinition, type FilterWerte, type Spalte,
} from '../../muster';
import { RANG_SEITE, msrZiele, useMsrExport } from './kopf';

const GERAET_TEXT: Record<string, string> = {
  fancoil: 'FanCoil', heizkuehldecke: 'Heiz-/Kühldecke', thermostat: 'Thermostat',
  praesenz: 'Präsenz', vsr: 'VSR', fensterantrieb: 'Fensterantrieb',
};

/**
 * Reiter „Raumautomation" — Räume mit Geräten (VDI 3813), Funktionen als
 * Marken, Datenpunkte Klemme/Bus, Filter Geschoss/Quadrant. Die Excel-Liste steht
 * seit TM-8a in der Exportleiste im Kopf (`useMsrExport`).
 */
export function Raumautomation({ api, projektId, raeume }: {
  api: ApiClient; projektId: string; raeume: MsrRaumZeile[];
}) {
  const [werte, setWerte] = useState<FilterWerte>({ geschoss: '', quadrant: '' });
  useMsrExport(RANG_SEITE, () => [msrZiele.raumautomation(api, projektId)], [api, projektId]);

  const geschosse = useMemo(() => Array.from(new Set(
    raeume.map((r) => r.geschoss).filter((g): g is string => !!g))).sort(), [raeume]);
  const quadranten = useMemo(() => Array.from(new Set(
    raeume.map((r) => r.quadrant).filter((q): q is string => !!q))).sort(), [raeume]);

  const filter: FilterDefinition[] = useMemo(() => [
    {
      schluessel: 'geschoss', beschriftung: 'Geschoss', art: 'auswahl',
      optionen: geschosse.map((g) => ({ wert: g, beschriftung: g })),
    },
    {
      schluessel: 'quadrant', beschriftung: 'Quadrant', art: 'auswahl',
      optionen: quadranten.map((q) => ({ wert: q, beschriftung: q })),
    },
  ], [geschosse, quadranten]);

  const gefiltert = raeume
    .filter((r) => !werte.geschoss || r.geschoss === werte.geschoss)
    .filter((r) => !werte.quadrant || r.quadrant === werte.quadrant);


  const geraeteText = (geraete: Record<string, number>) =>
    Object.entries(geraete)
      .filter(([, n]) => n > 0)
      .map(([k, n]) => `${GERAET_TEXT[k] ?? k} ${n}`)
      .join(' · ') || '—';

  const spalten: Spalte<MsrRaumZeile>[] = [
    { schluessel: 'raumnummer', titel: 'Raum', wert: (r) => r.raumnummer, immerSichtbar: true },
    { schluessel: 'raumname', titel: 'Name', wert: (r) => r.raumname ?? null },
    { schluessel: 'geschoss', titel: 'Geschoss', wert: (r) => r.geschoss ?? null },
    { schluessel: 'quadrant', titel: 'Quadrant', wert: (r) => r.quadrant ?? null },
    { schluessel: 'aspKennung', titel: 'ASP', wert: (r) => r.aspKennung ?? null },
    { schluessel: 'geraete', titel: 'Geräte', wert: (r) => geraeteText(r.geraete) },
    {
      schluessel: 'funktionen', titel: 'Funktionen', sortierbar: false,
      wert: (r) => r.funktionen.join(' '),
      inhalt: (r) => (r.funktionen.length === 0 ? '—' : (
        <span>
          {r.funktionen.map((f, i) => (
            <Fragment key={f}>{i > 0 && ' '}<StatusBadge zustand="neutral" text={f} klein /></Fragment>
          ))}
        </span>
      )),
    },
    { schluessel: 'datenpunkteKlemme', titel: 'Klemme', art: 'zahl',
      wert: (r) => r.datenpunkteKlemme },
    { schluessel: 'datenpunkteBus', titel: 'Bus', art: 'zahl',
      wert: (r) => r.datenpunkteBus },
  ];

  return (
    <>
      <FilterBar filter={filter} werte={werte} beiAenderung={setWerte} inLeiste={2} />

      <DataTable
        name="Raumautomation"
        beschreibung="Räume mit Raumautomationsgeräten und -funktionen nach VDI 3813."
        spalten={spalten}
        zeilen={gefiltert}
        schluessel={(r) => r.id}
        dicht
        ausfuhr
        ausfuhrDateiname="msr-raumautomation"
        klebendeErsteSpalte
        leer={(
          <EmptyState
            titel={raeume.length === 0
              ? 'Noch kein Raum mit Raumautomation abgeleitet.'
              : 'Kein Raum passt zu diesen Filtern.'}
            erklaerung={raeume.length === 0
              ? 'Die Raumautomation entsteht aus dem Ableitungslauf über Raumbuch und '
                + 'Gewerkemodelle.'
              : `${raeume.length} Räume sind vorhanden, keiner erfüllt die gesetzten Filter.`}
            aktion={raeume.length > 0 ? (
              <button type="button" className="m-knopf"
                onClick={() => setWerte({ geschoss: '', quadrant: '' })}>
                Filter zurücksetzen
              </button>
            ) : undefined}
          />
        )}
      />
    </>
  );
}
